import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageCircle, Send, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { eventService } from '@/services/eventService';

interface Message {
  id: string;
  cuerpo: string;
  tipo: 'INCOMING' | 'OUTGOING';
  incluye_botones?: boolean;
  created_at: string;
}

interface GuestMessageHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guestId: string;
  guestName: string;
  guestPhone: string;
  eventId: string;
}

export const GuestMessageHistoryModal: React.FC<GuestMessageHistoryModalProps> = ({
  open,
  onOpenChange,
  guestId,
  guestName,
  guestPhone,
  eventId
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [includeButtons, setIncludeButtons] = useState(false);
  const { toast } = useToast();

  // Load messages when modal opens
  useEffect(() => {
    if (open && guestId) {
      loadMessages();
    }
  }, [open, guestId]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      
      const messagesData = await eventService.getGuestMessages(guestId);
      
      // Convert the dates to strings for the component
      const formattedMessages = messagesData.map(msg => ({
        ...msg,
        created_at: msg.created_at.toISOString()
      }));
      
      setMessages(formattedMessages);
    } catch (error) {
      console.error('Error loading message history:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el historial de mensajes',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    try {
      setSending(true);
      
      await eventService.sendGuestMessage(guestId, eventId, newMessage, includeButtons);
      
      // Clear the message input
      setNewMessage('');
      
      // Reload messages to show the sent message
      await loadMessages();
      
      toast({
        title: 'Mensaje enviado',
        description: 'El mensaje ha sido enviado correctamente'
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: 'No se pudo enviar el mensaje',
        variant: 'destructive'
      });
    } finally {
      setSending(false);
    }
  };

  const formatMessageDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { 
        addSuffix: true,
        locale: es
      });
    } catch (error) {
      return 'Fecha desconocida';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <MessageCircle className="w-5 h-5 mr-2" />
            Historial de mensajes - {guestName}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            <p><span className="font-medium">Teléfono:</span> {guestPhone}</p>
          </div>
          
          <ScrollArea className="h-[300px] rounded border p-4">
            {loading ? (
              <div className="flex justify-center items-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No hay mensajes en el historial
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <div 
                    key={message.id} 
                    className={`p-3 rounded-lg max-w-[80%] ${
                      message.tipo === 'OUTGOING' 
                        ? 'bg-blue-100 ml-auto' 
                        : 'bg-gray-100 mr-auto'
                    }`}
                  >
                    <p className="text-sm">{message.cuerpo}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatMessageDate(message.created_at)}
                      {message.incluye_botones && ' · Con botones'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          
          <div className="space-y-2">                       
            <div className="flex items-center space-x-2">
              <Textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Escribe un mensaje..."
                className="min-h-[80px]"
              />
              <Button 
                className="h-10 px-3"
                onClick={handleSendMessage}
                disabled={sending || !newMessage.trim()}
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 